'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { trackEvent } from '@/utils/analytics';

/**
 * Advanced User Behavior Tracking Component
 * Tracks comprehensive user interactions for detailed analytics
 */
export default function UserBehaviorTracking() {
  const pathname = usePathname();
  const sessionStartRef = useRef<number>(Date.now());
  const pageStartRef = useRef<number>(Date.now());
  const clickCountRef = useRef<number>(0);
  const lastClickTimeRef = useRef<number>(0);
  const lastClickElementRef = useRef<HTMLElement | null>(null);
  const rageClickCountRef = useRef<number>(0);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userActiveRef = useRef<boolean>(true);
  const mouseMovementsRef = useRef<number>(0);
  const keyPressesRef = useRef<number>(0);

  useEffect(() => {
    // Track session start
    trackEvent('session_start', {
      session_start_time: new Date().toISOString(),
      referrer: document.referrer || 'direct',
      landing_page: pathname,
    });

    // Track page view with performance metrics
    if (typeof window !== 'undefined' && window.performance) {
      const perfData = window.performance.timing;
      const loadTime = perfData.loadEventEnd - perfData.navigationStart;

      trackEvent('page_performance', {
        page_load_time: loadTime,
        dom_ready_time: perfData.domContentLoadedEventEnd - perfData.navigationStart,
        first_paint_time: perfData.responseStart - perfData.navigationStart,
        page_path: pathname,
      });
    }

    // Clean up on unmount
    return () => {
      // Track session end
      const sessionDuration = Math.round((Date.now() - sessionStartRef.current) / 1000);
      trackEvent('session_end', {
        session_duration_seconds: sessionDuration,
        total_clicks: clickCountRef.current,
        total_mouse_movements: mouseMovementsRef.current,
        total_key_presses: keyPressesRef.current,
        exit_page: pathname,
      });
    };
  }, []);

  useEffect(() => {
    // Reset page start time on route change
    pageStartRef.current = Date.now();

    // Track page navigation
    trackEvent('page_navigation', {
      from_page: document.referrer,
      to_page: pathname,
      navigation_type: window.performance?.navigation?.type || 'unknown',
    });

    // Track time spent on previous page
    return () => {
      const timeOnPage = Math.round((Date.now() - pageStartRef.current) / 1000);
      trackEvent('time_on_page', {
        page_path: pathname,
        time_seconds: timeOnPage,
      });
    };
  }, [pathname]);

  useEffect(() => {
    // Track all clicks with detailed information
    const handleClick = (e: MouseEvent) => {
      clickCountRef.current += 1;
      const target = e.target as HTMLElement;
      const now = Date.now();

      // Get element details
      const elementType = target.tagName.toLowerCase();
      const elementText = target.textContent?.trim().substring(0, 50) || '';
      const elementId = target.id || '';
      const elementClass = target.className || '';
      const elementHref = target instanceof HTMLAnchorElement ? target.href : '';

      // Detect rage clicks (rapid repeated clicks on same element)
      if (
        lastClickElementRef.current === target &&
        now - lastClickTimeRef.current < 1000
      ) {
        rageClickCountRef.current += 1;
        if (rageClickCountRef.current >= 3) {
          trackEvent('rage_click', {
            element_type: elementType,
            element_text: elementText,
            element_id: elementId,
            click_count: rageClickCountRef.current,
            page_location: pathname,
          });
        }
      } else {
        rageClickCountRef.current = 0;
      }

      lastClickTimeRef.current = now;
      lastClickElementRef.current = target;

      // Track click details
      trackEvent('element_click', {
        element_type: elementType,
        element_text: elementText,
        element_id: elementId,
        element_class: elementClass,
        element_href: elementHref,
        click_x: e.clientX,
        click_y: e.clientY,
        page_location: pathname,
        timestamp: new Date().toISOString(),
      });

      // Track specific element types
      if (elementType === 'a') {
        trackEvent('link_click', {
          link_text: elementText,
          link_url: elementHref,
          is_external: !elementHref.includes(window.location.hostname),
        });
      } else if (elementType === 'button' || target.closest('button')) {
        const button = elementType === 'button' ? target : target.closest('button');
        trackEvent('button_click', {
          button_text: button?.textContent?.trim() || '',
          button_id: button?.id || '',
          page_location: pathname,
        });
      }

      // Detect dead clicks (clicks that don't do anything)
      setTimeout(() => {
        const hasChanged =
          window.location.pathname !== pathname ||
          document.activeElement !== document.body;

        if (!hasChanged && elementType !== 'input' && elementType !== 'textarea') {
          trackEvent('dead_click', {
            element_type: elementType,
            element_text: elementText,
            page_location: pathname,
          });
        }
      }, 300);
    };

    // Track user idle/active states
    const resetIdleTimer = () => {
      userActiveRef.current = true;
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }

      idleTimeoutRef.current = setTimeout(() => {
        userActiveRef.current = false;
        trackEvent('user_idle', {
          page_location: pathname,
          idle_start_time: new Date().toISOString(),
        });
      }, 60000); // 1 minute of inactivity
    };

    // Track mouse movements and reset the idle timer, throttled to once per animation
    // frame so it doesn't compete with the main thread on every raw mousemove event
    let mouseMoveRafId: number | null = null;
    const handleMouseMove = () => {
      if (mouseMoveRafId !== null) return;
      mouseMoveRafId = requestAnimationFrame(() => {
        mouseMoveRafId = null;
        mouseMovementsRef.current += 1;
        resetIdleTimer();
      });
    };

    // Track keyboard activity
    const handleKeyPress = (e: KeyboardEvent) => {
      keyPressesRef.current += 1;
      userActiveRef.current = true;

      // Track specific key combinations
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'f') {
          trackEvent('search_intent', {
            action: 'ctrl_f_pressed',
            page_location: pathname,
          });
        } else if (e.key === 'c') {
          trackEvent('copy_action', {
            page_location: pathname,
          });
        }
      }
    };

    // Track form interactions
    const handleFormFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        trackEvent('form_field_focus', {
          field_type: (target as HTMLInputElement).type || 'textarea',
          field_name: (target as HTMLInputElement).name || '',
          field_id: target.id || '',
          page_location: pathname,
        });
      }
    };

    // Track form errors
    const handleFormInvalid = (e: Event) => {
      const target = e.target as HTMLInputElement;
      trackEvent('form_validation_error', {
        field_type: target.type || '',
        field_name: target.name || '',
        field_id: target.id || '',
        validation_message: target.validationMessage || '',
        page_location: pathname,
      });
    };

    // Track back/forward navigation
    const handlePopState = () => {
      trackEvent('browser_navigation', {
        action: 'back_or_forward',
        page_location: pathname,
      });
    };

    // Track page visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        const timeVisible = Math.round((Date.now() - pageStartRef.current) / 1000);
        trackEvent('page_hidden', {
          page_location: pathname,
          time_visible_seconds: timeVisible,
        });
      } else {
        trackEvent('page_visible', {
          page_location: pathname,
        });
        pageStartRef.current = Date.now();
      }
    };

    // Track errors
    const handleError = (event: ErrorEvent) => {
      trackEvent('javascript_error', {
        error_message: event.message,
        error_source: event.filename,
        error_line: event.lineno,
        error_column: event.colno,
        page_location: pathname,
      });
    };

    // Track unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      trackEvent('unhandled_promise_rejection', {
        reason: String(event.reason),
        page_location: pathname,
      });
    };

    // Add all event listeners
    document.addEventListener('click', handleClick);
    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('keypress', handleKeyPress);
    document.addEventListener('focus', handleFormFocus, true);
    document.addEventListener('invalid', handleFormInvalid, true);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Initialize idle tracking (mousemove already resets it via handleMouseMove above)
    resetIdleTimer();
    document.addEventListener('keypress', resetIdleTimer);
    document.addEventListener('click', resetIdleTimer);

    // Cleanup
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('keypress', handleKeyPress);
      document.removeEventListener('focus', handleFormFocus, true);
      document.removeEventListener('invalid', handleFormInvalid, true);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      document.removeEventListener('keypress', resetIdleTimer);
      document.removeEventListener('click', resetIdleTimer);

      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      if (mouseMoveRafId !== null) {
        cancelAnimationFrame(mouseMoveRafId);
      }
    };
  }, [pathname]);

  // Track viewport size and orientation
  useEffect(() => {
    const trackViewport = () => {
      trackEvent('viewport_info', {
        width: window.innerWidth,
        height: window.innerHeight,
        orientation: window.innerWidth > window.innerHeight ? 'landscape' : 'portrait',
        device_pixel_ratio: window.devicePixelRatio || 1,
      });
    };

    trackViewport();
    window.addEventListener('resize', trackViewport);
    window.addEventListener('orientationchange', trackViewport);

    return () => {
      window.removeEventListener('resize', trackViewport);
      window.removeEventListener('orientationchange', trackViewport);
    };
  }, []);

  return null; // This component doesn't render anything
}
